<template>
  <div class="admin-container">
    <div class="admin-header">
      <h1>Create Lesson Plans</h1>
      <div class="action-buttons">
        <button
          class="preview-button"
          @click="previewLesson"
          :disabled="isSubmitting"
        >
          <div
            v-if="isSubmitting && savingType === 'preview'"
            class="spinner"
          ></div>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
            <circle cx="12" cy="12" r="3"></circle>
          </svg>
          Preview
        </button>
        <button
          class="draft-button"
          @click="saveLesson('draft')"
          :disabled="isSubmitting"
        >
          <div
            v-if="isSubmitting && savingType === 'draft'"
            class="spinner"
          ></div>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"
            ></path>
            <polyline points="17 21 17 13 7 13 7 21"></polyline>
            <polyline points="7 3 7 8 15 8"></polyline>
          </svg>
          {{
            isSubmitting && savingType === "draft"
              ? "Saving..."
              : "Save as Draft"
          }}
        </button>
        <button
          class="publish-button"
          @click="saveLesson('published')"
          :disabled="isSubmitting"
        >
          <div
            v-if="isSubmitting && savingType === 'published'"
            class="spinner"
          ></div>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"
            ></path>
            <polyline points="17 21 17 13 7 13 7 21"></polyline>
            <polyline points="7 3 7 8 15 8"></polyline>
          </svg>
          {{
            isSubmitting && savingType === "published"
              ? "Publishing..."
              : "Save as Published"
          }}
        </button>
      </div>
    </div>

    <!-- Error Display -->
    <div v-if="showValidationErrors" class="validation-errors">
      <div class="error-header">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <h3>Please fix the following errors:</h3>
        <button class="close-error" @click="showValidationErrors = false">
          ×
        </button>
      </div>
      <ul>
        <li v-for="(error, field) in validationErrors" :key="field">
          {{ error }}
        </li>
      </ul>
    </div>

    <div class="admin-content-layout">
      <div class="admin-main-content">
        <TitleSection
          ref="titleSectionRef"
          @update:title="updateTitle"
          @update:subtitle="updateSubtitle"
          @update:duration="updateDuration"
        ></TitleSection>
        <LessonPlanEditorSection ref="editorRef" @tabs-updated="updateTabs" />
      </div>
      <div
        class="admin-right-sidebar"
        :class="{ 'mobile-open': rightSidebarOpen }"
      >
        <div class="admin-sidebar-header">
          <button
            class="close-sidebar-button"
            @click="toggleRightSidebar"
            v-if="isMobile"
          >
            Close Sidebar
          </button>
        </div>
        <div class="admin-sidebar-content">
          <!-- Sidebar content here -->
          <RightSidebar
            ref="rightSidebarRef"
            @cover-image-updated="updateCoverImage"
            @tag-sections-updated="updateTagSections"
            @permalink-updated="updatePermalink"
          ></RightSidebar>
        </div>
      </div>
    </div>

    <!-- Snackbar Notification -->
    <div v-if="notification.show" class="snackbar" :class="notification.type">
      <div class="snackbar-content">
        <svg
          v-if="notification.type === 'success'"
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22 4 12 14.01 9 11.01"></polyline>
        </svg>
        <svg
          v-else
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <span>{{ notification.message }}</span>
      </div>
      <button class="snackbar-close" @click="closeNotification">×</button>
    </div>

    <button
      class="floating-button"
      @click="toggleRightSidebar"
      v-if="isMobile && !rightSidebarOpen"
    >
      Open Sidebar
    </button>
    <div
      class="right-sidebar-overlay"
      v-if="rightSidebarOpen && isMobile"
      @click="toggleRightSidebar"
    ></div>
  </div>
</template>

<script setup>
/* eslint-disable */
import LessonPlanEditorSection from "@/components/admin/lesson-plans/LessonPlanEditorSection.vue";
import RightSidebar from "@/components/admin/lesson-plans/RightSidebar.vue";
import TitleSection from "@/components/admin/lesson-plans/TitleSection.vue";
import lessonPlanService from "@/services/lessonPlanService";
import { useUnsavedChanges } from "@/composeables/useUnsavedChanges";
import { inject, onMounted, onUnmounted, ref } from "vue";
import { useRouter } from "vue-router";

const router = useRouter();
const rightSidebarOpen = ref(false);
const isMobile = ref(window.innerWidth < 991);
const editorRef = ref(null);
const rightSidebarRef = ref(null);
const titleSectionRef = ref(null);
const isSubmitting = ref(false);
const savingType = ref(null); // Tracks which save operation is in progress
const validationErrors = ref({});
const showValidationErrors = ref(false);

const notify = inject("notify");

// Notification system
const notification = ref({
  show: false,
  message: "",
  type: "success", // success or error
  timeout: null,
});

const closeNotification = () => {
  notification.value.show = false;
  if (notification.value.timeout) {
    clearTimeout(notification.value.timeout);
  }
};
// Lesson data
const lessonData = ref({
  title: "",
  subtitle: "",
  duration: "",
  coverImage: null,
  permalink: "",
  tagSections: [],
  tabs: [],
  materials: [],
});

// Initialize unsaved changes tracking
const { markAsChanged, markAsSaved, setInitialLoading } = useUnsavedChanges(
  lessonData,
  {
    confirmMessage:
      "You have unsaved changes. Are you sure you want to leave this page? Your changes will be lost.",
  }
);

// Update methods for two-way binding
const updateTitle = (title) => {
  lessonData.value.title = title;
  markAsChanged();
};

const updateSubtitle = (subtitle) => {
  lessonData.value.subtitle = subtitle;
  markAsChanged();
};

const updateDuration = (duration) => {
  lessonData.value.duration = duration;
  markAsChanged();
};

const updateCoverImage = (coverImage) => {
  lessonData.value.coverImage = coverImage;
  markAsChanged();
};

const updatePermalink = (permalink) => {
  lessonData.value.permalink = permalink;
  markAsChanged();
};

const updateTagSections = (tagSections) => {
  lessonData.value.tagSections = tagSections;
  markAsChanged();
};

const updateTabs = (tabs, materials) => {
  lessonData.value.tabs = tabs;

  // Update instructional materials if provided
  if (materials && Array.isArray(materials)) {
    lessonData.value.materials = materials;
  }
  markAsChanged();
};

const handleResize = () => {
  isMobile.value = window.innerWidth < 991;
};

// Load data from localStorage when the component mounts
const loadFromLocalStorage = () => {
  try {
    setInitialLoading(true);
    const savedData = localStorage.getItem("lessonPreviewData");
    if (savedData) {
      const parsedData = JSON.parse(savedData);

      // Validate that we have the expected structure before using the data
      if (parsedData) {
        // Update the lesson data
        lessonData.value = parsedData;

        // Notify child components to update their state
        // This will happen in the next tick after the components are mounted
        if (titleSectionRef.value) {
          titleSectionRef.value.updateFromParent(
            parsedData.title || "",
            parsedData.subtitle || "",
            parsedData.duration || ""
          );
        }

        if (editorRef.value) {
          editorRef.value.updateFromParent(
            parsedData.tabs || [],
            parsedData.materials || []
          );
        }

        if (rightSidebarRef.value) {
          rightSidebarRef.value.updateFromParent(
            parsedData.coverImage || null,
            parsedData.tagSections || [],
            parsedData.permalink || ""
          );
        }

        // Mark as saved after loading from localStorage
        setTimeout(() => {
          markAsSaved();
          setInitialLoading(false);
        }, 100);
      }
    } else {
      setInitialLoading(false);
    }
  } catch (error) {
    console.error("Error loading lesson data from localStorage:", error);
    setInitialLoading(false);
  }
};

onMounted(() => {
  window.addEventListener("resize", handleResize);

  // Load data from localStorage
  setTimeout(() => {
    loadFromLocalStorage();
  }, 100); // Small delay to ensure components are mounted
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
});

function toggleRightSidebar() {
  rightSidebarOpen.value = !rightSidebarOpen.value;
}

function previewLesson() {
  // Check if lesson has been saved
  if (!lessonData.value.id) {
    // Show notification that lesson needs to be saved first
    notify.warning(
      "Please save your lesson as a draft or published before previewing.",
      "Save Required"
    );
    return;
  }

  // Navigate to preview page with lesson ID
  router.push({
    name: "LessonPlansPreviewPage",
    params: { id: lessonData.value.id },
  });
}

// Generate a slug from title
const generateSlug = (title) => {
  return title
    .toLowerCase()
    .replace(/[^\w\s]/gi, "")
    .replace(/\s+/g, "-");
};

// Format categories for API
const formatCategories = (tagSections) => {
  if (!tagSections || !tagSections.length) return [];

  return tagSections
    .filter(
      (section) =>
        section.id !== "standards" &&
        section.selected &&
        section.selected.length
    )
    .map((section) => {
      const termIds = section.selected
        .map((tag) => {
          try {
            const parsed = typeof tag === "string" ? JSON.parse(tag) : tag;
            return parsed.id;
          } catch (e) {
            return null;
          }
        })
        .filter((id) => id !== null);

      return {
        category_id: section.id,
        term_id: termIds,
      };
    });
};

// Validate lesson data
const validateLessonData = () => {
  const errors = {};

  if (!lessonData.value.title.trim()) {
    errors.title = "Title is required";
  }

  if (!lessonData.value.duration) {
    errors.duration = "Duration is required";
  }

  if (!lessonData.value.coverImage) {
    errors.coverImage = "Cover image is required";
  }

  // Add validation for specific required tag sections
  const requiredSectionTitles = ["Tech Domain", "Topic", "Software", "Grade"];

  const missingSections = lessonData.value.tagSections
    .filter(
      (section) =>
        requiredSectionTitles.includes(section.title) &&
        (!section.selected || section.selected.length === 0)
    )
    .map((section) => section.title);

  if (missingSections.length > 0) {
    errors.tagSections = `Please select at least one tag in each of these required categories: ${missingSections.join(
      ", "
    )}`;
  }

  validationErrors.value = errors;

  // Show errors UI if there are errors
  if (Object.keys(errors).length > 0) {
    showValidationErrors.value = true;
  }

  return Object.keys(errors).length === 0;
};

// Save lesson
const saveLesson = async (status) => {
  if (isSubmitting.value) return;

  // Validate data
  if (!validateLessonData()) {
    return;
  }

  try {
    isSubmitting.value = true;
    savingType.value = status;
    validationErrors.value = {};
    showValidationErrors.value = false;

    // Prepare data for API
    const formData = new FormData();
    formData.append("title", lessonData.value.title);
    formData.append("slug", generateSlug(lessonData.value.title));
    formData.append("content", JSON.stringify(lessonData.value.tabs));
    formData.append("duration", lessonData.value.duration);
    formData.append("status", status);
    formData.append("permalink", lessonData.value.permalink);

    let slideData = null;

    // Add slide URL if slides are attached
    if (editorRef.value && editorRef.value.isSlideAttached()) {
      slideData = editorRef.value.getSlideData();
      if (slideData) {
        formData.append("slide_url", slideData.url);
      }
    }

    formData.append(
      "date",
      new Date()
        .toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
        .replace(/\//g, "-")
    );

    // Process cover image
    if (lessonData.value.coverImage) {
      // If the image is a base64 string, convert it to a file
      if (lessonData.value.coverImage.startsWith("data:image")) {
        const imageBlob = await fetch(lessonData.value.coverImage).then((r) =>
          r.blob()
        );
        const imageFile = new File(
          [imageBlob],
          `cover-image-${Date.now()}.jpg`,
          { type: "image/jpeg" }
        );
        formData.append("thumbnail", imageFile);
      } else {
        // If it's already a file, just append it
        formData.append("thumbnail", lessonData.value.coverImage);
      }
    }

    // Process categories
    const categories = formatCategories(lessonData.value.tagSections);
    formData.append("categories", JSON.stringify(categories));

    // Call API
    const response = await lessonPlanService.createLesson(formData);
    const newLesson = response?.data?.data;

    // Store the lesson ID for preview functionality
    if (newLesson && newLesson.id) {
      lessonData.value.id = newLesson.id;
    }

    if (newLesson && lessonData.value.materials?.length > 0) {
      // filter undefined materials
      const materials = lessonData.value.materials
        .filter((material) => material !== undefined)
        .map((material, index) => ({
          lesson_id: newLesson.id,
          name: material.title,
          url: material.url,
          order: index,
        }));

      if (materials.length > 0) {
        await lessonPlanService.createBulkMaterials(materials);
      }
    }

    if (newLesson && slideData) {
      await lessonPlanService.assignSlideToLesson({
        lesson_id: newLesson.id,
        code: slideData.code,
        replace_existing: false,
      });
    }

    const successMessage = `Lesson plan ${
      status === "published" ? "published" : "saved as draft"
    } successfully!`;

    notify.success(successMessage, "Success");

    // Mark as saved after successful save
    markAsSaved();

    router.push({ name: "LessonPlansListPage" });
  } catch (error) {
    console.error("Error saving lesson plan:", error.response);
    const errorMessage =
      error?.response?.data?.message ||
      "Failed to save lesson plan. Please try again.";
    notify.error(errorMessage, "Error");
    showValidationErrors.value = true;
    validationErrors.value = {
      api: errorMessage,
    };
  } finally {
    isSubmitting.value = false;
    savingType.value = null;
  }
};
</script>

<style scoped>
.admin-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.action-buttons {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.preview-button,
.draft-button,
.publish-button,
.clear-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.preview-button {
  background-color: var(--primary-blue);
  color: white;
}

.preview-button:hover {
  opacity: 0.8;
}

.draft-button {
  background-color: #6c757d;
  color: white;
}

.draft-button:hover {
  background-color: #5a6268;
}

.publish-button {
  background-color: #28a745;
  color: white;
}

.publish-button:hover {
  background-color: #218838;
}

.clear-button {
  background-color: #e74c3c;
  color: white;
}

.clear-button:hover {
  background-color: #c0392b;
}

/* Button disabled state */
button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Spinner for loading state */
.action-buttons .spinner {
  width: 16px !important;
  height: 16px !important;
  border: 2px solid rgba(255, 255, 255, 0.3) !important;
  border-radius: 50% !important;
  border-top-color: #fff !important;
  animation: spin 0.8s linear infinite !important;
  background-color: transparent !important;
  box-shadow: none !important;
  margin: 0 !important;
  display: inline-block !important;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Validation errors */
.validation-errors {
  margin-bottom: 20px;
  border: 1px solid #f8d7da;
  border-radius: 4px;
  background-color: #fff5f6;
  overflow: hidden;
}

.error-header {
  display: flex;
  align-items: center;
  background-color: #f8d7da;
  color: #721c24;
  padding: 10px 15px;
  font-weight: bold;
  gap: 10px;
}

.error-header h3 {
  margin: 0;
  flex-grow: 1;
}

.close-error {
  background: none;
  border: none;
  color: #721c24;
  font-size: var(--fs-big);
  cursor: pointer;
}

.validation-errors ul {
  margin: 0;
  padding: 15px 15px 15px 40px;
  color: #721c24;
}

.validation-errors li {
  margin-bottom: 5px;
}

/* Snackbar Styles */
.snackbar {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  min-width: 300px;
  max-width: 80%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-radius: 4px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  animation: slide-up 0.3s ease-out;
}

.snackbar.success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.snackbar.error {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.snackbar-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.snackbar-close {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: inherit;
  padding: 0;
  margin-left: 15px;
}

@keyframes slide-up {
  from {
    transform: translate(-50%, 100%);
    opacity: 0;
  }
  to {
    transform: translate(-50%, 0);
    opacity: 1;
  }
}

@media (max-width: 768px) {
  .admin-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .action-buttons {
    margin-top: 10px;
    width: 100%;
    flex-direction: column;
  }

  .attachment-buttons {
    width: 100%;
    margin-bottom: 10px;
  }

  .preview-button,
  .draft-button,
  .publish-button,
  .attachment-button {
    width: 100%;
    justify-content: center;
  }

  .snackbar {
    width: 90%;
    bottom: 10px;
  }
}
</style>
